import { computed, ref, type Ref } from 'vue';
import { countCustomisations, getLatestSaved } from '../../lib/callModContent';
import {
  getLatestDraft,
  getLatestSaved as getLatestSavedFromAuthors
} from '../lib/callModContentAuthors';

export type DiscoveryCourseRow = CustomReport['list'][number] & {
  baseCourseId: number;
};

export function courseKey(firmCode: string, courseId: number): string {
  return `${firmCode}_${courseId}`;
}

export function parseCourseIds(input: string): number[] {
  return input
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0)
    .map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));
}

export function useBulkCustomisationDiscovery() {
  const courseIdsInput = ref('');
  const loadingCustomisations = ref(false);
  const customisationsReports = ref<Map<number, CustomReport>>(new Map());
  const includeDuplicates = ref(true);
  const selectedCourses = ref<Set<string>>(new Set());
  const courseUnsavedChanges = ref<
    Map<
      string,
      { hasUnsavedChanges: boolean; draftAuthor?: string; draftDate?: number }
    >
  >(new Map());
  const checkingUnsavedChanges = ref(false);
  const baseCourseLoadErrors = ref<Map<number, string>>(new Map());

  const flattenCustomisations = (
    filter?: (course: DiscoveryCourseRow) => boolean
  ): DiscoveryCourseRow[] => {
    const rows: DiscoveryCourseRow[] = [];

    customisationsReports.value.forEach((report, baseCourseId) => {
      report.list.forEach(course => {
        if (!includeDuplicates.value && course.courseId !== baseCourseId) {
          return;
        }
        const row: DiscoveryCourseRow = { ...course, baseCourseId };
        if (!filter || filter(row)) {
          rows.push(row);
        }
      });
    });

    return rows.sort((a, b) => b.updatedDate - a.updatedDate);
  };

  const totalSummary = computed(() => {
    let totalBaseCourses = 0;
    let totalCustomisations = 0;

    customisationsReports.value.forEach(report => {
      totalBaseCourses++;
      totalCustomisations += report.total;
    });

    return { totalBaseCourses, totalCustomisations };
  });

  const isCourseSelected = (course: { firmCode: string; courseId: number }) =>
    selectedCourses.value.has(courseKey(course.firmCode, course.courseId));

  const selectAllCourses = (event: Event, courses: DiscoveryCourseRow[]) => {
    const { checked } = event.target as HTMLInputElement;
    if (checked) {
      courses.forEach(course => {
        selectedCourses.value.add(courseKey(course.firmCode, course.courseId));
      });
    } else {
      selectedCourses.value.clear();
    }
  };

  const toggleCourseSelection = (course: {
    firmCode: string;
    courseId: number;
  }) => {
    const key = courseKey(course.firmCode, course.courseId);
    if (selectedCourses.value.has(key)) {
      selectedCourses.value.delete(key);
    } else {
      selectedCourses.value.add(key);
    }
  };

  const allCoursesSelected = (courses: DiscoveryCourseRow[]) => {
    if (courses.length === 0) return false;
    return courses.every(course => isCourseSelected(course));
  };

  const selectedCoursesCount = computed(() => selectedCourses.value.size);

  const checkUnsavedChanges = async (course: {
    firmCode: string;
    courseId: number;
  }) => {
    const key = courseKey(course.firmCode, course.courseId);

    try {
      const [latestDraft, latestSaved] = await Promise.all([
        getLatestDraft({
          courseId: course.courseId,
          firmCode: course.firmCode
        }),
        getLatestSavedFromAuthors({
          courseId: course.courseId,
          firmCode: course.firmCode
        })
      ]);

      const hasUnsavedChanges =
        latestDraft &&
        (latestSaved
          ? latestDraft.metadata.updatedDate > latestSaved.metadata.createdDate
          : true);

      courseUnsavedChanges.value.set(key, {
        hasUnsavedChanges: !!hasUnsavedChanges,
        draftAuthor: latestDraft?.author,
        draftDate: latestDraft?.metadata.updatedDate
      });
    } catch {
      courseUnsavedChanges.value.set(key, { hasUnsavedChanges: false });
    }
  };

  const checkAllUnsavedChanges = async (courses: DiscoveryCourseRow[]) => {
    if (courses.length === 0) return;

    checkingUnsavedChanges.value = true;
    courseUnsavedChanges.value.clear();

    try {
      await Promise.all(courses.map(course => checkUnsavedChanges(course)));
    } finally {
      checkingUnsavedChanges.value = false;
    }
  };

  const getUnsavedChangesStatus = (course: {
    firmCode: string;
    courseId: number;
  }) =>
    courseUnsavedChanges.value.get(courseKey(course.firmCode, course.courseId));

  const getUnsavedChangesTooltip = (course: {
    firmCode: string;
    courseId: number;
  }) => {
    const status = getUnsavedChangesStatus(course);
    if (!status?.hasUnsavedChanges) return 'No unsaved changes';

    const draftDate = status.draftDate
      ? new Date(Number(status.draftDate)).toLocaleString()
      : 'Unknown';
    const author = status.draftAuthor || 'Unknown';

    return `Has unsaved changes by ${author} on ${draftDate}`;
  };

  const hasAnyUnsavedChanges = (courses: Ref<DiscoveryCourseRow[]>) =>
    computed(() =>
      Array.from(courseUnsavedChanges.value.entries()).some(([key, status]) => {
        const course = courses.value.find(
          c => courseKey(c.firmCode, c.courseId) === key
        );
        return course && isCourseSelected(course) && status.hasUnsavedChanges;
      })
    );

  const unsavedChangesCount = (courses: Ref<DiscoveryCourseRow[]>) =>
    computed(
      () =>
        Array.from(courseUnsavedChanges.value.entries()).filter(
          ([key, status]) => {
            const course = courses.value.find(
              c => courseKey(c.firmCode, c.courseId) === key
            );
            return (
              course && isCourseSelected(course) && status.hasUnsavedChanges
            );
          }
        ).length
    );

  const clearDiscovery = () => {
    customisationsReports.value.clear();
    baseCourseLoadErrors.value.clear();
    courseUnsavedChanges.value.clear();
    selectedCourses.value.clear();
  };

  const loadCustomisations = async () => {
    const courseIds = parseCourseIds(courseIdsInput.value);
    if (courseIds.length === 0) return;

    clearDiscovery();
    loadingCustomisations.value = true;

    try {
      for (const courseId of courseIds) {
        try {
          const baseCourse = await getLatestSaved({ courseId });

          if (!baseCourse) {
            baseCourseLoadErrors.value.set(courseId, 'No base course found.');
            continue;
          }

          if (baseCourse.activityName === 'Conversational Learning') {
            baseCourseLoadErrors.value.set(
              courseId,
              'Conversational Learning courses cannot be used.'
            );
            continue;
          }

          const report = await countCustomisations(courseId);
          customisationsReports.value.set(courseId, report);
        } catch (error) {
          baseCourseLoadErrors.value.set(
            courseId,
            error instanceof Error ? error.message : 'Unknown error'
          );
        }
      }
    } finally {
      loadingCustomisations.value = false;
    }
  };

  return {
    courseIdsInput,
    loadingCustomisations,
    customisationsReports,
    includeDuplicates,
    selectedCourses,
    courseUnsavedChanges,
    checkingUnsavedChanges,
    baseCourseLoadErrors,
    totalSummary,
    flattenCustomisations,
    parseCourseIds,
    loadCustomisations,
    clearDiscovery,
    isCourseSelected,
    selectAllCourses,
    toggleCourseSelection,
    allCoursesSelected,
    selectedCoursesCount,
    checkUnsavedChanges,
    checkAllUnsavedChanges,
    getUnsavedChangesStatus,
    getUnsavedChangesTooltip,
    hasAnyUnsavedChanges,
    unsavedChangesCount,
    courseKey
  };
}
